import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Button } from "primereact/button";
import { ComplaintInvoiceModel } from "../../models/complaints/complaint-model";

interface ComplaintInvoiceProps {
  invoiceData: ComplaintInvoiceModel[];
}

const ComplaintInvoice: React.FC<ComplaintInvoiceProps> = ({ invoiceData }) => {
  const invoiceRef = useRef(null);

  const handleInvoicePrint = useReactToPrint({
    content: () => invoiceRef.current,
    documentTitle: 'Complaint Invoice Details',
    pageStyle: `@page { margin: 5mm; }`
  });

  if (!invoiceData || invoiceData.length === 0) {
    return <div>No invoice data found.</div>;
  }

  const firstInvoice = invoiceData[0];

  const invoiceItems = invoiceData.map((item, index) => ({
    id: index + 1,
    productName: item.invoiceProductName,
    unitPrice: item.invoiceUnitPrice,
    invoicedQuantity: item.invoicedQuantity,
    measurementTypeCode: item.measurementTypeCode,
    gst: item.invoiceGST,
    totalAmount: item.totalAmount
  }));

  const headerTemplate = (
    <div className="grid">
      <div className="col-6">
        <img src={process.env.PUBLIC_URL + "/assests/images/DesiriLogo.png"} width={100} className="mb-3" />
        <div><strong>From:</strong></div>
        <div className="text-primary">
          <div>Desiri Naturals Pvt Ltd</div> 
          {firstInvoice.poS_Address1}, {firstInvoice.poS_Address2}, {firstInvoice.poS_Landmark},<br />
          {firstInvoice.poS_CityName}, {firstInvoice.poS_StateName}, {firstInvoice.poS_CountryName}, {firstInvoice.poS_Zipcode}<br />
          GST #: {firstInvoice.gst_Number || "29AAGCD5864J1Z4"}
        </div>
        <br />
        <div><strong>To:</strong></div>
         <span className="text-primary">{firstInvoice.from_UserName}</span>
        <br />
        <br />
        <div>Delivery Address:  <span className="text-primary"> {firstInvoice.deliveryAddress}, {firstInvoice.deliveryAreaName}, {firstInvoice.deliverySubDistrictName},<br />
          {firstInvoice.deliveryCityName}, {firstInvoice.deliveryStateName}, {firstInvoice.deliveryCountryName}<br />
         </span>
          </div>
        <div>Pin: <span className="text-primary">{firstInvoice.zipcode}</span></div> <br />
        <div>Mobile: <span className="text-primary">{firstInvoice.phoneNumber}</span></div>
      </div>
      
      <div className="col-5 ml-8">
      <div className="col-6">
        <div className="grid mt-7 ">
        <div> Order #: <span className="text-primary">{firstInvoice.orderID}</span></div> 
        </div>
        <div className="grid pt-3">
        <div>Invoice #: <span className="text-primary">{firstInvoice.invoiceNo}</span></div>
        </div>
        <div className="grid pt-3">
        <div>Invoice Date: 
          <span className="text-primary">
            {new Date(firstInvoice.invoicedDate).toLocaleDateString("en-gb", {
              day: "2-digit",
              month: "short",
              year: "numeric"
            })}
          </span>
        </div>
        </div>
        <div className="grid pt-3">
        <div>Invoice Amount: <span className="text-primary">{Helpers.formatAmountInINR(firstInvoice.invoiceGrossAmount)}</span></div>
        </div>
        <div className="grid pt-3">
        <div>Payment Mode: <span className="text-primary">{firstInvoice.invoicePaymentModeName}</span></div>
        </div>
      </div>
    </div>
   </div>
  );

  const footerTemplate = (
    <div className="grid pt-2">
      <div className="col-7"></div>
      <div className="col-5 pt-2">
        <div>Taxable Amount:
          <span className="text-primary " style={{ float: "right" }}>
            {Helpers.formatAmountInINR(firstInvoice.invoiceTaxableAmount)}
          </span>
        </div>
        <div className="pt-2">Tax Amount:
          <span className="text-primary" style={{ float: "right" }}>
            {Helpers.formatAmountInINR(firstInvoice.invoiceTaxAmount)}
          </span>
        </div>
        <div className="pt-2">Courier Charges:
          <span className="text-primary" style={{ float: "right" }}>
            {Helpers.formatAmountInINR(firstInvoice.courierCharges)}
          </span>
        </div>
        {firstInvoice.invoicePaymentGatewayCharges > 0 && (
          <div className="pt-2">Payment Gateway Charges:
            <span className="text-primary" style={{ float: "right" }}>
              {Helpers.formatAmountInINR(firstInvoice.invoicePaymentGatewayCharges)}
            </span>
          </div>
        )}
        {firstInvoice.discountAmount > 0 && (
          <div className="pt-2">Discount Amount:
            <span className="text-primary" style={{ float: "right" }}>
              {Helpers.formatAmountInINR(firstInvoice.discountAmount)}
            </span>
          </div>
        )}
        <div className="pt-2">Total Amount:
          <span className="text-primary" style={{ float: "right" }}>
            {Helpers.formatAmountInINR(firstInvoice.invoiceGrossAmount)}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="grid mb-2">
        <div className="col">
          Complaint Invoice Details
          <Button 
            icon="pi pi-print" 
            style={{ float: "right" }}
            onClick={handleInvoicePrint} 
            rounded 
            text 
            severity="info" 
            aria-label="Print" 
          />
        </div>
      </div>

      <div ref={invoiceRef}>
        <DataTable
          value={invoiceItems}
          className="no-flex"
          header={headerTemplate}
          footer={footerTemplate}
          size="small"
        >
          <Column field="productName" header="Name" />
          <Column field="unitPrice" header="Price" body={(row) => Helpers.formatAmountInINR(row.unitPrice)} />
          <Column field="invoicedQuantity" header="Quantity" body={(row) => `${row.invoicedQuantity} (${row.measurementTypeCode})`} />
          <Column header="Amount" body={(row) => Helpers.formatAmountInINR(row.invoicedQuantity * row.unitPrice)} />
          <Column header="Tax" body={(row) => Helpers.formatAmountInINR(row.invoicedQuantity * row.unitPrice * row.gst / 100)} />
          <Column field="totalAmount" header="Total" body={(row) => Helpers.formatAmountInINR(row.totalAmount)} />
        </DataTable>
      </div>
    </>
  );
};

export default ComplaintInvoice;
